import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, Save, X, AlertTriangle, Maximize2, Undo2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';
import { formatGs } from '@/utils/helpers';
import L from 'leaflet';
import { MapContainer, TileLayer, Polygon, Marker, Polyline, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const emptyZone = { name: '', branch_id: '', delivery_price: 0, minimum_order_amount: 0, active: true };
const ASUNCION_CENTER = [-25.2867, -57.6470];
const CARTO_LIGHT = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

// Draggable vertex icon
const vertexIcon = (active) => L.divIcon({
  className: '',
  html: `<div style="width:14px;height:14px;border-radius:50%;background:${active ? '#E84C1C' : '#fff'};border:2px solid ${active ? '#fff' : '#E84C1C'};cursor:grab;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const midpointIcon = L.divIcon({
  className: '',
  html: `<div style="width:10px;height:10px;border-radius:50%;background:#E84C1C40;border:2px dashed #E84C1C;cursor:pointer"></div>`,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

function calcAreaM2(points) {
  if (points.length < 3) return 0;
  // Shoelace formula on projected coords (approximate for small areas)
  const R = 6371000;
  const toRad = Math.PI / 180;
  const refLat = points[0][0] * toRad;
  const projected = points.map(([lat, lng]) => [
    (lng - points[0][1]) * toRad * R * Math.cos(refLat),
    (lat - points[0][0]) * toRad * R
  ]);
  let area = 0;
  for (let i = 0; i < projected.length; i++) {
    const j = (i + 1) % projected.length;
    area += projected[i][0] * projected[j][1];
    area -= projected[j][0] * projected[i][1];
  }
  return Math.abs(area / 2);
}

function formatArea(m2) {
  if (m2 < 10000) return `${Math.round(m2).toLocaleString('es-PY')} m²`;
  return `${(m2 / 1000000).toFixed(2)} km²`;
}

// Interactive polygon editor
function PolygonEditor({ points, setPoints, selectedVertex, setSelectedVertex }) {
  const map = useMap();

  useMapEvents({
    click(e) {
      if (points.length === 0 || !e.originalEvent.shiftKey) {
        setPoints(prev => [...prev, [e.latlng.lat, e.latlng.lng]]);
        setSelectedVertex(points.length);
      }
    },
  });

  // Fit map to polygon on first load
  useEffect(() => {
    if (points.length >= 3) {
      const bounds = L.latLngBounds(points.map(p => L.latLng(p[0], p[1])));
      map.fitBounds(bounds.pad(0.3));
    }
  }, []); // eslint-disable-line

  const handleVertexDrag = useCallback((idx, e) => {
    const { lat, lng } = e.target.getLatLng();
    setPoints(prev => prev.map((p, i) => i === idx ? [lat, lng] : p));
  }, [setPoints]);

  const handleMidpointClick = useCallback((idx) => {
    const p1 = points[idx];
    const p2 = points[(idx + 1) % points.length];
    const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
    setPoints(prev => [...prev.slice(0, idx + 1), mid, ...prev.slice(idx + 1)]);
    setSelectedVertex(idx + 1);
  }, [points, setPoints, setSelectedVertex]);

  // Midpoints between each pair of vertices
  const midpoints = useMemo(() => {
    if (points.length < 2) return [];
    return points.map((p, i) => {
      const next = points[(i + 1) % points.length];
      return [(p[0] + next[0]) / 2, (p[1] + next[1]) / 2];
    });
  }, [points]);

  return (
    <>
      {/* Filled polygon */}
      {points.length >= 3 && (
        <Polygon
          positions={points}
          pathOptions={{ color: '#E84C1C', fillColor: '#E84C1C', fillOpacity: 0.15, weight: 2 }}
        />
      )}
      {/* Edge lines when < 3 points */}
      {points.length === 2 && (
        <Polyline positions={points} pathOptions={{ color: '#E84C1C', weight: 2 }} />
      )}
      {/* Draggable vertices */}
      {points.map((pos, idx) => (
        <Marker
          key={`v-${idx}-${pos[0]}-${pos[1]}`}
          position={pos}
          icon={vertexIcon(selectedVertex === idx)}
          draggable
          eventHandlers={{
            click: () => setSelectedVertex(idx),
            dragend: (e) => handleVertexDrag(idx, e),
          }}
        />
      ))}
      {/* Midpoint markers for adding new vertices */}
      {points.length >= 2 && midpoints.map((pos, idx) => (
        <Marker
          key={`m-${idx}`}
          position={pos}
          icon={midpointIcon}
          eventHandlers={{ click: () => handleMidpointClick(idx) }}
        />
      ))}
    </>
  );
}

export default function ZonesPage() {
  const [zones, setZones] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyZone);
  const [polyPoints, setPolyPoints] = useState([]);
  const [originalPoints, setOriginalPoints] = useState([]);
  const [selectedVertex, setSelectedVertex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchData = () => {
    setLoading(true);
    Promise.all([api.get('/zonas-entrega'), api.get('/sucursales')]).then(([z, b]) => {
      setZones(z.data.zonas);
      setBranches(b.data.sucursales);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(fetchData, []);

  // Track changes
  useEffect(() => {
    if (!editing) return;
    const changed = JSON.stringify(polyPoints) !== JSON.stringify(originalPoints);
    setHasChanges(changed);
  }, [polyPoints, originalPoints, editing]);

  const startEdit = (zone) => {
    setEditing(zone || 'new');
    setSelectedVertex(-1);
    if (zone && zone !== 'new') {
      setForm({ name: zone.name, branch_id: zone.branch_id, delivery_price: zone.delivery_price, minimum_order_amount: zone.minimum_order_amount, active: zone.active });
      if (zone.geojson_polygon?.coordinates?.[0]) {
        const pts = zone.geojson_polygon.coordinates[0].slice(0, -1).map(([lng, lat]) => [lat, lng]);
        setPolyPoints(pts);
        setOriginalPoints(pts);
      }
    } else {
      setForm(emptyZone);
      setPolyPoints([]);
      setOriginalPoints([]);
    }
    setHasChanges(false);
  };

  const cancelEdit = () => {
    if (hasChanges && !window.confirm('Tenés cambios sin guardar. ¿Descartar?')) return;
    setEditing(null);
    setHasChanges(false);
  };

  const deleteVertex = () => {
    if (selectedVertex < 0 || selectedVertex >= polyPoints.length) return;
    if (polyPoints.length <= 3) { toast.error('Mínimo 3 puntos.'); return; }
    setPolyPoints(prev => prev.filter((_, i) => i !== selectedVertex));
    setSelectedVertex(-1);
  };

  const save = async () => {
    if (polyPoints.length < 3) { toast.error('El polígono necesita al menos 3 puntos.'); return; }
    if (!form.branch_id) { toast.error('Seleccioná una sucursal.'); return; }
    if (!form.name.trim()) { toast.error('El nombre es obligatorio.'); return; }
    try {
      const coords = polyPoints.map(([lat, lng]) => [lng, lat]);
      if (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1]) {
        coords.push(coords[0]);
      }
      const payload = {
        ...form,
        branch_id: parseInt(form.branch_id),
        delivery_price: parseFloat(form.delivery_price) || 0,
        minimum_order_amount: parseFloat(form.minimum_order_amount) || 0,
        geojson_polygon: { type: 'Polygon', coordinates: [coords] },
      };
      if (editing === 'new') {
        await api.post('/zonas-entrega', payload);
        toast.success('Zona creada');
      } else {
        await api.put(`/zonas-entrega/${editing.id}`, payload);
        toast.success('Zona actualizada');
      }
      setEditing(null);
      setHasChanges(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar zona?')) return;
    try { await api.delete(`/zonas-entrega/${id}`); toast.success('Eliminada'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setHasChanges(true); };
  const area = calcAreaM2(polyPoints);

  return (
    <div data-testid="admin-zones-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Zonas de Entrega</h2>
          <p className="text-sm text-zinc-500">{zones.length} zonas</p>
        </div>
        {!editing && (
          <button onClick={() => startEdit('new')} className="flex items-center gap-2 bg-[#E84C1C] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d43f12] transition-colors" data-testid="zone-add-btn">
            <Plus size={16} /> Nueva
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-6 space-y-3" data-testid="zone-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{editing === 'new' ? 'Nueva zona' : `Editar: ${typeof editing === 'object' ? editing.name : ''}`}</h3>
            {hasChanges && (
              <span className="flex items-center gap-1 text-xs text-amber-400"><AlertTriangle size={12} /> Cambios sin guardar</span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div><Label className="text-xs text-zinc-500">Nombre</Label><Input value={form.name} onChange={e => set('name', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" data-testid="zone-name" /></div>
            <div>
              <Label className="text-xs text-zinc-500">Sucursal</Label>
              <select data-testid="zone-branch" value={form.branch_id} onChange={e => set('branch_id', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 h-9 text-sm text-zinc-300 outline-none mt-1">
                <option value="">Seleccionar...</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-zinc-500">Precio envío (Gs.)</Label><Input type="number" value={form.delivery_price} onChange={e => set('delivery_price', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" data-testid="zone-price" /></div>
            <div><Label className="text-xs text-zinc-500">Pedido mín (Gs.)</Label><Input type="number" value={form.minimum_order_amount} onChange={e => set('minimum_order_amount', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" data-testid="zone-min" /></div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="accent-[#E84C1C]" /> Activa</label>
            <span className="text-zinc-500">|</span>
            <span className="text-zinc-400 text-xs">{polyPoints.length} vértices</span>
            {area > 0 && <span className="text-xs font-mono text-[#E84C1C]" data-testid="zone-area">{formatArea(area)}</span>}
            <div className="ml-auto flex gap-2">
              {selectedVertex >= 0 && polyPoints.length > 3 && (
                <button onClick={deleteVertex} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded bg-red-500/10" data-testid="zone-delete-vertex">
                  <Trash2 size={12} /> Eliminar punto
                </button>
              )}
              <button onClick={() => { setPolyPoints(originalPoints); setSelectedVertex(-1); }} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white px-2 py-1 rounded bg-zinc-800" data-testid="zone-undo">
                <Undo2 size={12} /> Restaurar
              </button>
              <button onClick={() => setPolyPoints([])} className="text-xs text-zinc-500 hover:text-white px-2 py-1 rounded bg-zinc-800">Limpiar</button>
            </div>
          </div>

          {/* Map editor */}
          <div className="h-[400px] rounded-xl overflow-hidden border border-zinc-700 shadow-lg" data-testid="zone-map-editor">
            <MapContainer center={ASUNCION_CENTER} zoom={13} className="h-full w-full" zoomControl={true} attributionControl={false}>
              <TileLayer url={CARTO_LIGHT} />
              <PolygonEditor
                points={polyPoints}
                setPoints={setPolyPoints}
                selectedVertex={selectedVertex}
                setSelectedVertex={setSelectedVertex}
              />
              {/* Show other zones as reference */}
              {zones.filter(z => typeof editing === 'object' ? z.id !== editing.id : true).map(z => (
                z.geojson_polygon?.coordinates?.[0] && (
                  <Polygon key={z.id} positions={z.geojson_polygon.coordinates[0].map(([lng, lat]) => [lat, lng])} pathOptions={{ color: '#888', fillColor: '#888', fillOpacity: 0.08, dashArray: '5,5', weight: 1 }} />
                )
              ))}
            </MapContainer>
          </div>
          <p className="text-xs text-zinc-500">Click en el mapa para agregar vértices. Arrastrá los puntos para mover. Click en los puntos medios (punteados) para agregar un vértice en el borde. Seleccioná un vértice y usá "Eliminar punto".</p>

          {/* Action buttons - always visible */}
          <div className="flex gap-2 sticky bottom-0 bg-zinc-900 py-2 -mx-4 px-4 border-t border-zinc-800/50">
            <button onClick={save} className="flex items-center gap-1.5 bg-[#E84C1C] text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-[#d43f12] transition-colors" data-testid="zone-save">
              <Save size={14} /> Guardar zona
            </button>
            <button onClick={cancelEdit} className="flex items-center gap-1 text-sm text-zinc-400 px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors">
              <X size={14} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Zones table */}
      <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800/50 text-zinc-500 text-xs uppercase tracking-wider">
            <th className="text-left p-3 font-medium">Nombre</th><th className="text-left p-3 font-medium">Sucursal</th><th className="text-left p-3 font-medium">Envío</th><th className="text-left p-3 font-medium">Pedido Mín</th><th className="text-center p-3 font-medium">Activa</th><th className="text-right p-3 font-medium">Acciones</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Cargando...</td></tr> :
            zones.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-zinc-500">No hay zonas.</td></tr> :
            zones.map(z => {
              const branch = branches.find(b => b.id === z.branch_id);
              return (
                <tr key={z.id} data-testid={`zone-row-${z.id}`} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                  <td className="p-3 font-medium">{z.name}</td>
                  <td className="p-3 text-zinc-400 text-xs">{branch?.name || `#${z.branch_id}`}</td>
                  <td className="p-3 text-zinc-300">{formatGs(z.delivery_price)}</td>
                  <td className="p-3 text-zinc-300">{formatGs(z.minimum_order_amount)}</td>
                  <td className="p-3 text-center">{z.active ? <span className="text-emerald-400 text-xs">Sí</span> : <span className="text-red-400 text-xs">No</span>}</td>
                  <td className="p-3 text-right space-x-2">
                    <button onClick={() => startEdit(z)} className="text-zinc-400 hover:text-[#E84C1C] transition-colors" data-testid={`zone-edit-${z.id}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(z.id)} className="text-zinc-400 hover:text-red-400 transition-colors" data-testid={`zone-delete-${z.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
